import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, MessageSquare, ClipboardList } from 'lucide-react';

interface Product {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    link: string;
    color: string;
    shadowColor: string;
}

const products: Product[] = [
    {
        id: 'calendar',
        title: 'Calendar',
        description: 'Visual scheduling with swim lanes and real-time patient flow',
        icon: <Calendar className="w-6 h-6 text-white" />,
        link: '/products/calendar',
        color: 'from-blue-500 to-blue-600',
        shadowColor: 'shadow-blue-500/50'
    },
    {
        id: 'online-booking',
        title: 'Online Booking',
        description: 'Embeddable appointment booking widget for your website',
        icon: <Calendar className="w-6 h-6 text-white" />,
        link: '/products/online-booking',
        color: 'from-green-500 to-green-600',
        shadowColor: 'shadow-green-500/50'
    },
    {
        id: 'mobile-forms',
        title: 'Mobile Forms',
        description: 'Digital check-in forms clients complete on their phone',
        icon: <ClipboardList className="w-6 h-6 text-white" />,
        link: '/products/mobile-forms',
        color: 'from-orange-500 to-orange-600',
        shadowColor: 'shadow-orange-500/50'
    },
    {
        id: 'two-way-texting',
        title: '2-Way Texting',
        description: 'Send and receive SMS & MMS with clients',
        icon: <MessageSquare className="w-6 h-6 text-white" />,
        link: '/products/two-way-texting',
        color: 'from-purple-500 to-purple-600',
        shadowColor: 'shadow-purple-500/50'
    }
];

interface MetallicStackProps {
    standalone?: boolean;
    className?: string;
}

const MetallicStack: React.FC<MetallicStackProps> = ({ standalone = true, className = '' }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const content = (
        <div className={`max-w-7xl mx-auto ${className}`}>
            {standalone && (
                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-4 tracking-tight">
                        Our Product Suite
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-dark-text-secondary max-w-2xl mx-auto">
                        A complete ecosystem of AI-powered tools designed for modern veterinary practices.
                    </p>
                </div>
            )}

            <div className={`relative flex items-center justify-center ${standalone ? 'min-h-[450px]' : 'min-h-[400px] py-10'}`}>
                <div className="relative w-full max-w-md h-[400px]">
                    {products.map((product, index) => {
                        const isHovered = hoveredIndex === index;

                        // Calculate z-index: 
                        // Default: Top items have higher z-index (stacking down)
                        // Hovered: Moves to very top (50)
                        const zIndex = isHovered ? 50 : products.length - index;

                        return (
                            <Link
                                key={product.id}
                                to={product.link}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                                className={`
                  absolute left-0 right-0 mx-auto
                  bg-gradient-to-r ${product.color}
                  rounded-2xl p-6 border border-white/20 dark:border-white/30
                  cursor-pointer
                  transition-all duration-300 ease-out
                  ${isHovered ? 'shadow-2xl dark:shadow-black/50' : 'shadow-xl dark:shadow-black/40'}
                `}
                                style={{
                                    // Increased spacing to 100px to ensure hit-targets are accessible
                                    top: `${index * 90}px`,
                                    zIndex: zIndex,
                                    // Simplified transform: Scale down lower items, lift hovered item
                                    transform: isHovered
                                        ? `scale(1.05) translateY(-10px)`
                                        : `scale(${1 - index * 0.03}) translateY(0px)`,
                                    // Solid opacity - no transparency
                                    opacity: 1,
                                }}
                            >
                                {/* Metallic Shine Effect */}
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/20 via-transparent to-black/10 pointer-events-none" />
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/20 to-transparent opacity-30 pointer-events-none" />

                                {/* Edge highlight */}
                                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/30 pointer-events-none" />

                                <div className="relative flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center shadow-inner border border-white/20">
                                            {product.icon}
                                        </div>
                                        <div className="text-left">
                                            <h3 className="text-xl font-bold text-white mb-1 drop-shadow-md">{product.title}</h3>
                                            <p className="text-white/90 text-sm font-medium">{product.description}</p>
                                        </div>
                                    </div>

                                    <div
                                        className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group-hover:translate-x-1 border border-white/10"
                                    >
                                        <ArrowRight className="w-5 h-5 text-white" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    if (!standalone) {
        return content;
    }

    return (
        <section className="py-16 px-6 bg-gradient-to-b from-white to-slate-50 dark:from-dark-bg-primary dark:to-dark-bg-secondary overflow-hidden">
            {content}
        </section>
    );
};

export default MetallicStack;
